import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

import { Cache } from 'aws-amplify/utils';
import { CacheQuery as ClientCacheQuery } from '../../modules/client-office/app/shared';
import { CacheQuery as ManagementCacheQuery } from '../../modules/management-office/app/typescript';
import { environment } from '../../environment';

async function request<T>(config: AxiosRequestConfig): Promise<
  AxiosResponse<
    T & {
      readonly message: string;
      readonly status: string;
      readonly code: number;
      readonly errors?: ReadonlyArray<string>;
    }
  >['data']
> {
  try {
    const { headers, ...restConfig } = config;

    const response = await axios({
      baseURL: environment.api.base,
      method: config.method ?? 'get',
      headers: {
        ...headers,
      },
      // Cookie-only auth: send the HttpOnly auth cookie (flies same-origin anyway; explicit for robustness).
      withCredentials: true,
      ...restConfig,
    });

    return response.data;
  } catch (error) {
    console.error((error as AxiosError)?.message);

    // R10: detect logout by HTTP status + `code`, NOT `message`. In mode:prod known.filter masks the message
    // to 'Internal server error' but preserves both status and code, so message-based detection breaks there.
    // The `status === 403` guard is essential: code 1 is ALSO the backend's generic 500-error code (used in 250+
    // places), so a code-only check logs the user out on any unrelated server error. Only an auth failure from a
    // guard is 403 + code 1 (not authenticated) / 1009 (token expired). code 1003 (role/access-denial from
    // guard-coverage) is a 403 but must NOT log out — show the error instead.
    const status = (error as AxiosError).response?.status;
    const code = ((error as AxiosError).response?.data as { code?: number })?.code;

    if (status === 403 && (code === 1 || code === 1009)) {
      Cache.removeItem(ManagementCacheQuery.USER);
      Cache.removeItem(ClientCacheQuery.USER);
      localStorage.removeItem('local-auth-co');
      localStorage.removeItem('local-auth-mo');
      typeof window !== 'undefined' && window?.location.reload();
    }

    throw error;
  }
}

export { request };
